// The graph builder: the palette, the canvas's steps and branch labels, and the branch drawer.
import type { ReactNode } from "react";

import type { NodeKind, StepOutcome } from "../../../../catalog/types";
import { plural, rich } from "../../../helpers";

const p = plural("en");

export default {
  kind: { prompt: "Prompt", approval: "Approval", orchestrate: "Subtasks", end: "End" } satisfies Record<NodeKind, string>,
  hint: { prompt: "An agent step", approval: "Ask the task's owner", orchestrate: "Run the approved plan's subtasks", end: "Finish the task" } satisfies Record<NodeKind, string>,
  addKind: { prompt: "Add prompt step", approval: "Add approval step", orchestrate: "Add subtasks step", end: "Add end step" } satisfies Record<NodeKind, string>,
  addStep: "Add a step",
  autoLayout: "Auto-layout",
  recentre: "Re-centre",
  emptyCanvas: (prompt: ReactNode) =>
    rich("Drag a ", prompt, " from the palette, or click it. Connect a step's bottom handle to another's top to add a branch."),
  node: {
    promptWithModel: (model: string) => `Prompt · ${model}`,
    approvalOptions: (n: number) => p(n, { one: "Approval · # option", other: "Approval · # options" }),
    endFailed: "End · failed",
    endSucceeded: "End · succeeded",
    start: "Start",
    issues: (n: number) => p(n, { one: "# issue", other: "# issues" }),
    now: "Now",
  },
  edgeLabel: {
    otherwise: "otherwise",
    outputIn: (key: string, values: string) => `${key} in ${values}`,
    reported: (key: string) => `${key} reported`,
    notReported: (key: string) => `no ${key}`,
  },
  conditionType: { outcome: "Step outcome", output: "Step output", answer: "Approval answer", otherwise: "Otherwise" },
  outcome: {
    succeeded: "Succeeded",
    failed: "Failed",
    timed_out: "Timed out",
    skipped: "Skipped (pre-execution exit 3)",
  } satisfies Record<StepOutcome, string>,
  op: { equals: "equals", in: "is one of", exists: "reported or not" },
  branch: {
    title: "Branch",
    delete: "Delete branch",
    takenWhen: "Taken when",
    condition: "Condition",
    order: "Order",
    tried: (position: number, of: number) => `Tried ${position} of ${of}`,
    orderHelp: "Branches leaving a step are tried in order; the first that matches is taken. “Otherwise” always goes last.",
    earlier: "Earlier",
    later: "Later",
    otherwiseHint: "Taken when no earlier branch matched.",
    outcome: "Outcome",
    answer: "Answer",
    outputKey: "Output key",
    declared: (keys: string) => `Declared by the step: ${keys}.`,
    noneDeclared: "The step declares no outputs yet.",
    test: "Test",
    whenKeyIs: "When the key is",
    isReported: "reported",
    isNotReported: "not reported",
    values: "Values (comma-separated)",
    value: "Value",
  },
};
